import { useEffect, useState } from "react"
import api from "../../services/api"
import PricingCard from "../../components/payment/PricingCard"
import PricingComparisonTable from "../../components/payment/PricingComparisonTable"
import WhatsAppChat from "../../components/support/WhatsAppChat"
import PageMeta from "../../components/pagesHelmet/PageMeta"
import Navbar from "../../components/Layout/Navbar"
import { useNavigate } from "react-router-dom"
import Footer from "../../components/Layout/Footer"

export default function PricingPage() {
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [billingType, setBillingType] = useState("monthly")

  const BRAND = {
    primary: "#3B82F6",
    primaryDark: "#2563EB",
    primarySoft: "rgba(59, 130, 246, 0.1)",
    success: "#22C55E",
    warning: "#F59E0B",
    muted: "#6B7280",
    text: "#1F2937",
  }

  useEffect(() => {
    const fetchData = async () => {
      try {

        const plansRes = await api.get("/subscription/plans")
        setPlans(
          Object.entries(plansRes.data).map(([key, plan]) => ({
            key,
            title: plan.title,
            price: plan.pricing,
            features: plan.features,
            limit: plan.limit,
            monthlyUrls: plan.monthlyUrls,
            trafficLimit: plan.trafficLimit,
          }))
        )
      } catch (err) {
        console.error("Failed to fetch plans:", err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const navigate = useNavigate();

  const isToken = localStorage.getItem("token");

  const openPayment = () => {

  if (!isToken) {
    // If user is not logged in, redirect to login
    navigate("/login");
    return;
  }
  // If logged in, redirect to upgrade page
  navigate("/account/upgrade");
  }

  if (loading) {
    return (
        <>
        <div className="container py-5 text-center">
          <div className="spinner-border" role="status" style={{ width: "3rem", height: "3rem", color: BRAND.primary }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading plans...</p>
        </div>
        </>
    )
  }

  return (
    <>
    <Navbar />
      <PageMeta
        title="Pricing Plans | U2LR"
        description="Choose the perfect U2LR plan. Compare features, monthly/yearly pricing, and upgrade easily."
      />

      {/* Hero */}
      <section className="text-center py-5 bg-light">
        <div className="container">
          <h1 className="display-5 fw-bold mb-3">Pricing Plans</h1>
          <p className="text-muted fs-5">
            Select a plan that suits your needs and start growing your links today.
          </p>
        </div>
      </section>

      {/* Billing Toggle */}
      <div className="text-center py-4">
        <div className="d-inline-flex align-items-center bg-white rounded-pill shadow-sm p-1" style={{ border: "1px solid #E5E7EB" }}>
          {["monthly", "yearly"].map((type) => (
            <button
              key={type}
              className={`btn btn-sm px-4 py-2 rounded-pill fw-medium ${billingType === type ? "text-white" : "btn-link text-dark"}`}
              style={
                billingType === type
                  ? { background: BRAND.primary, boxShadow: `0 2px 8px ${BRAND.primarySoft}`, border: "none" }
                  : { border: "none" }
              }
              onClick={() => setBillingType(type)}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
              {type === "yearly" && <span className="badge bg-success ms-2" style={{ fontSize: "0.7rem" }}>Save 40%</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="container py-5">
        <div className="row g-4 mb-5">
          {plans.map((plan) => (
            <div key={plan.key} className="col-md-6 col-lg-3">
              <PricingCard
                title={plan.title}
                price={plan.price}
                billingType={billingType}
                features={plan.features}
                limit={plan.limit}
                monthlyUrls={plan.monthlyUrls}
                trafficLimit={plan.trafficLimit}
                highlight={["premium", "dedicated"].includes(plan.key)}
                button={() => openPayment()}
              />
            </div>
          ))}
        </div>

        {/* Feature Comparison */}
        <div className="mt-5 pt-5">
          <div className="text-center mb-4">
            <h2 className="fw-bold mb-3" style={{ background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.success})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              Detailed Feature Comparison
            </h2>
            <p className="text-muted">Compare all features across different plans</p>
          </div>
          <PricingComparisonTable plans={plans} billingType={billingType} />
        </div>
      </div>

      <WhatsAppChat position="right" />
      <Footer />
    </>
  )
}
